/**
 * Web MIDI API integration for VueSynth (WAX).
 * Request MIDI access on load or on first user gesture; attach inputs to the synth when ready.
 *
 * Reserved for standard MIDI (do not assign to synth params; for future use):
 *   CC 0  = Bank Select
 *   CC 1  = Mod Wheel
 *   CC 2  = Breath
 *   CC 4  = Foot Controller
 *   CC 5  = Portamento Time
 *   CC 6  = Data Entry
 *   CC 10 = Pan
 *   CC 11 = Expression
 *   CC 64 = Sustain Pedal
 *   CC 65 = Portamento On/Off
 *   CC 66 = Sostenuto
 *   CC 67 = Soft Pedal
 *   CC 86–127 = reserved for other standard / future use
 *
 * Note: Pitch Bend is a separate message (status 0xE0, 14-bit), not a CC; can be handled later.
 */

const NOTE_ON = 0x90;
const NOTE_OFF = 0x80;
const CC = 0xb0;

/** CC numbers reserved for standard MIDI (mod wheel, sustain, etc.) – not mapped to synth params */
export const RESERVED_CC = Object.freeze([
  0, 1, 2, 4, 5, 6, 10, 11, 64, 65, 66, 67
]);

let midiAccessPromise = null;

/**
 * Request MIDI access.
 * @returns {Promise<MIDIAccess>}
 */
export function requestMIDIAccess() {
  if (!navigator.requestMIDIAccess) {
    return Promise.reject(new Error("Web MIDI API not supported"));
  }
  if (!midiAccessPromise) {
    midiAccessPromise = navigator.requestMIDIAccess();
  }
  return midiAccessPromise;
}

/**
 * CC number -> synth controller id map.
 * Uses CC 7 (Volume) + 12–63 + 68–85 so reserved standard CCs (mod wheel, sustain, etc.) stay free.
 * @type {Object<number, string>}
 */
export const DEFAULT_CC_MAP = {
  // Standard: Volume (keep for DAW/controller compatibility)
  7: "MASTER_GAIN",
  // 12–63: Oscillators, filter, envelopes, LFOs, first effects
  12: "OSC_1_TP",
  13: "OSC_1_OCT",
  14: "OSC_1_SEMI",
  15: "OSC_1_FINE",
  16: "OSC_1_PHASE",
  17: "OSC_1_VOICE",
  18: "OSC_1_SPRD",
  19: "OSC_1_RETR",
  20: "OSC_1_VOL",
  21: "OSC_1_PAN",
  22: "OSC_2_TP",
  23: "OSC_2_OCT",
  24: "OSC_2_SEMI",
  25: "OSC_2_FINE",
  26: "OSC_2_PHASE",
  27: "OSC_2_VOICE",
  28: "OSC_2_SPRD",
  29: "OSC_2_RETR",
  30: "OSC_2_VOL",
  31: "OSC_2_PAN",
  32: "OSC_3_VOL",
  33: "OSC_3_PAN",
  34: "FILTER_CUT",
  35: "FILTER_Q",
  36: "FILTER_EG",
  37: "FILTER_TP",
  38: "AMP_EG_ATT",
  39: "AMP_EG_DEC",
  40: "AMP_EG_SUS",
  41: "AMP_EG_REL",
  42: "EG_1_ATT",
  43: "EG_1_DEC",
  44: "EG_1_SUS",
  45: "EG_1_REL",
  46: "LFO_1_FC",
  47: "LFO_1_TP",
  48: "LFO_1_GAIN",
  49: "LFO_1_PHASE",
  50: "LFO_1_RETR",
  51: "LFO_2_FC",
  52: "LFO_2_TP",
  53: "LFO_2_GAIN",
  54: "LFO_2_PHASE",
  55: "LFO_2_RETR",
  56: "FX1_ON",
  57: "FX1_WET",
  58: "FX1_1",
  59: "FX1_2",
  60: "FX2_ON",
  61: "FX2_WET",
  62: "FX2_1",
  63: "FX2_2",
  // 64–67 reserved (Sustain, Portamento, Sostenuto, Soft)
  // 68–81: more effects, mod matrix
  68: "FX2_3",
  69: "FX2_4",
  70: "FX2_5",
  71: "FX3_ON",
  72: "FX3_WET",
  73: "FX3_1",
  74: "FX3_2",
  75: "FX4_ON",
  76: "FX4_WET",
  77: "FX4_1",
  78: "FX4_2",
  79: "FX5_ON",
  80: "FX5_1",
  81: "M_ROW_1",
  82: "M_ROW_3",
  83: "M_ROW_2",
  84: "M_ROW_4",
  85: "M_ROW_5"
};

/** Reverse map: controller id -> CC number (for sending automation to host) */
export const CONTROLLER_ID_TO_CC = Object.freeze(
  Object.entries(DEFAULT_CC_MAP).reduce((acc, [cc, id]) => {
    acc[id] = Number(cc);
    return acc;
  }, {})
);

/**
 * Send a single CC to all MIDI outputs (for DAW automation recording).
 * Only call this when the change came from the user; do not call when updating from incoming MIDI.
 * @param {MIDIAccess} access
 * @param {Object} synth
 * @param {string} controllerId
 * @param {number} value - control's native value (will be scaled to 0–127)
 * @param {Object<number, string>} [ccMap]
 */
export function sendCC(access, synth, controllerId, value, ccMap = DEFAULT_CC_MAP) {
  const ccNum = CONTROLLER_ID_TO_CC[controllerId];
  if (ccNum == null) return;
  const control = synth.getControl(controllerId);
  if (!control) return;
  const min = control.min !== undefined ? Number(control.min) : 0;
  const max = control.max !== undefined ? Number(control.max) : 1;
  const range = max - min;
  const normalized = range === 0 ? 0 : (value - min) / range;
  const data2 = Math.round(Math.max(0, Math.min(127, normalized * 127)));
  const outputs = Array.from(access.outputs.values());
  outputs.forEach(output => {
    try {
      output.send([CC, ccNum, data2]);
    } catch (err) {
      // port may be disconnected
    }
  });
}

/**
 * Attach MIDI inputs to the synth and return detach + sendCC for automation.
 * - Incoming CC: updates control value (UI + audio). Do not send CC back for these.
 * - User moves control: emit control:commit with { id, value }; listener should call sendCC so host can record.
 * @param {MIDIAccess} access
 * @param {Object} synth
 * @param {Object<number, string>} [ccMap]
 * @param {{ onNoteOn?: (note: number) => void, onNoteOff?: (note: number) => void, isControlBeingDragged?: (controllerId: string) => boolean }} [callbacks]
 * @returns {{ detach: Function, sendCC: (controllerId: string, value: number) => void }}
 */
export function attachMIDIToSynth(access, synth, ccMap = DEFAULT_CC_MAP, callbacks = {}) {
  const { onNoteOn, onNoteOff, isControlBeingDragged } = callbacks;
  const inputs = Array.from(access.inputs.values());
  const handlers = [];

  function handleMessage(e) {
    const [status, data1, data2] = e.data;
    const type = status & 0xf0;

    if (type === NOTE_ON && data2 > 0) {
      synth.triggerAttack(data1, undefined, data2 / 127);
      onNoteOn && onNoteOn(data1);
    } else if (type === NOTE_OFF || (type === NOTE_ON && data2 === 0)) {
      synth.triggerRelease(data1);
      onNoteOff && onNoteOff(data1);
    } else if (type === CC) {
      const controllerId = ccMap[data1];
      if (controllerId) {
        if (isControlBeingDragged && isControlBeingDragged(controllerId)) {
          return;
        }
        const control = synth.getControl(controllerId);
        if (control != null) {
          const min = control.min !== undefined ? Number(control.min) : 0;
          const max = control.max !== undefined ? Number(control.max) : 1;
          const value = min + (data2 / 127) * (max - min);
          control.value = value;
        }
      }
    }
  }

  inputs.forEach(input => {
    input.onmidimessage = handleMessage;
    handlers.push({ input });
  });

  function detach() {
    handlers.forEach(({ input }) => {
      input.onmidimessage = null;
    });
  }

  function sendCCForControl(controllerId, value) {
    sendCC(access, synth, controllerId, value, ccMap);
  }

  return { detach, sendCC: sendCCForControl };
}
